/**
 * C7 — Log in with Google, from any SynAnalytics or portal site:
 * 1. start: remember a nonce in this tab, then go to Google;
 * 2. Google returns to the main site's /oauth/google-login, which finishes
 *    and sends the browser back to where it started with a one-time grant;
 * 3. that site checks the nonce is the one it kept (so nobody can push a
 *    sign-in onto someone else's browser) and redeems the grant.
 */

import { adoptSession, apiCall, type SessionUser } from '../api.ts';

const NONCE_KEY = 'synanalytics.google-nonce';
const NONCE_BYTES = 24;

function newNonce(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(NONCE_BYTES));
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export async function startGoogleSignIn(): Promise<void> {
  const nonce = newNonce();
  sessionStorage.setItem(NONCE_KEY, nonce);
  const { url } = await apiCall<{ url: string }>('/auth/google/start', {
    method: 'POST',
    body: JSON.stringify({ origin: window.location.origin, nonce }),
  });
  window.location.assign(url);
}

/** On the main site's /oauth/google-login: returns where to send the browser. */
export async function finishGoogleSignIn(code: string, state: string): Promise<string> {
  const { redirect } = await apiCall<{ redirect: string }>('/auth/google/finish', {
    method: 'POST',
    body: JSON.stringify({ code, state }),
  });
  return redirect;
}

/** A grant in this page's address (#/google-signin?grant=…&nonce=…), taken out of it. */
export function takeGoogleGrant(): { grant: string; nonce: string } | null {
  const match = /^#\/google-signin\?(.*)$/.exec(window.location.hash);
  if (!match) return null;
  window.history.replaceState({}, '', window.location.pathname + window.location.search);
  const params = new URLSearchParams(match[1]);
  const grant = params.get('grant');
  const nonce = params.get('nonce');
  return grant && nonce ? { grant, nonce } : null;
}

export async function redeemGoogleGrant(input: {
  grant: string;
  nonce: string;
}): Promise<{ user: SessionUser } | { challengeToken: string }> {
  const kept = sessionStorage.getItem(NONCE_KEY);
  sessionStorage.removeItem(NONCE_KEY);
  if (!kept || kept !== input.nonce)
    throw new Error('That Google sign-in was not started here. Try again.');
  const data = await apiCall<{
    access_token?: string;
    refresh_token?: string;
    user: SessionUser;
    mfa_required?: boolean;
    challenge_token?: string;
  }>('/auth/google/grant', { method: 'POST', body: JSON.stringify({ grant: input.grant }) });
  if (data.mfa_required && data.challenge_token) return { challengeToken: data.challenge_token };
  adoptSession(data.access_token as string, data.refresh_token as string);
  return { user: data.user };
}
